import type { TransportItem } from '@grafana/faro-web-sdk';
// If `@grafana/faro-web-sdk` does not re-export TransportItem in the installed
// version, import it from '@grafana/faro-core' instead.

const EMAIL = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g;
// Hyphen- or space-separated SSN. The compact 9-digit form is intentionally NOT
// matched to avoid redacting the platform's many legitimate 9-digit numeric IDs
// (account / order numbers).
const SSN = /\b\d{3}[ -]\d{2}[ -]\d{4}\b/g;
const TOKEN_QS = /([?&])(token|access_token|api[_-]?key|password|secret|auth)=[^&#\s]*/gi;

export function scrubString(value: string | undefined): string | undefined {
  if (!value) return value;
  return value.replace(EMAIL, '[email]').replace(SSN, '[ssn]').replace(TOKEN_QS, '$1$2=[REDACTED]');
}

export function stripQuery(url: string | undefined): string | undefined {
  if (!url) return url;
  const i = url.indexOf('?');
  return i === -1 ? url : url.slice(0, i);
}

export function scrubTransportItem(item: TransportItem): TransportItem | null {
  const payload = item.payload as Record<string, unknown> | undefined;

  if (item.type === 'exception' && payload) {
    if (typeof payload.value === 'string') {
      payload.value = scrubString(payload.value);
    }
    const stacktrace = payload.stacktrace as { frames?: Array<{ filename?: string }> } | undefined;
    stacktrace?.frames?.forEach((frame) => {
      frame.filename = scrubString(frame.filename);
    });
  }

  if (item.type === 'log' && payload && typeof payload.message === 'string') {
    payload.message = scrubString(payload.message);
  }

  if (item.type === 'event' && payload) {
    const attributes = payload.attributes as Record<string, string | undefined> | undefined;
    if (attributes) {
      for (const key of Object.keys(attributes)) {
        attributes[key] = scrubString(attributes[key]);
      }
    }
  }

  const page = item.meta?.page as { url?: string } | undefined;
  if (page?.url) {
    page.url = stripQuery(page.url);
  }

  // NOTE: `trace` items (OTLP spans) can carry full request URLs (e.g. http.url with
  // query strings) in span attributes. We do NOT walk OTLP span attributes here — that
  // redaction is delegated to Alloy server-side processing (spec §11), which must be a
  // hard prerequisite before enabling Path C in any non-local environment.

  // We redact in place and always forward the item. The `| null` return type exists
  // to satisfy Faro's beforeSend contract (returning null would drop the event); we
  // never drop here — redaction is sufficient.
  return item;
}
